// What is this launch actually installing, and which engine runs it?
//
// One function, because the answer used to be spread across three expressions in
// launch.ts and a comment explaining why one of them stayed silent. That silence
// — an `--installer-tag` launch quietly leaving the engine on its baked version —
// is the trap that faked a "still broken" reading from a working system, so it is
// worth having the whole matrix in one place where it can be read at a glance.
//
// The matrix:
//
//   nothing                   → the release this CLI pins, engine = the pin's
//   --release X               → release X, engine = X's MANIFEST names it
//   --installer-tag/-image    → no release claim at all (unreleased-debug)
//   --release X + an override → refused; two answers to one question
//
// The split is deliberate and was the operator's call. Installing a past release
// and pointing at an arbitrary engine image are different needs that were
// conflated into one silent behaviour: `--release X` does the first one exactly
// (right engine, right manifest, honest stamp), and a raw override does the
// second one honestly, claiming nothing.
import { LocalCliError } from "./errors.js";
import type { SealedManifest } from "./manifest-seal.js";

export type LaunchTarget =
  | {
      kind: "released";
      /** Bare X.Y.Z. Passed to the engine, which derives the manifest URL from it. */
      version: string;
      /** `<ref>:<tag>` of the engine that runs this install. */
      engineImage: string;
      /** `sha256:<hex>` the engine must reproduce from its own fetch. */
      manifestSha256: string;
    }
  | {
      kind: "unreleased-debug";
      engineImage: string;
      /** Human-readable, names the flag that caused it. Printed at launch. */
      reason: string;
    };

export interface ResolveLaunchTargetArgs {
  /** `<ref>:<tag>` this CLI was built to launch, from installer/release-pin.json. */
  pinnedInstallerRef: string;
  /** Bare X.Y.Z this CLI was built to install. */
  pinnedPlatformVersion: string;
  /** `--release` — bare X.Y.Z or the platform-vX.Y.Z spelling. */
  release?: string;
  /** `--installer-tag`. */
  installerTag?: string;
  /** `--installer-image` (full ref, wins over the tag). */
  installerImage?: string;
  fetchSealed: (version: string) => Promise<SealedManifest>;
}

const BARE_VERSION = /^\d+\.\d+\.\d+(?:-canary\.(?:0|[1-9]\d*))?$/;

/** Accept either spelling of a release and return the bare one, or null. */
function bareVersionOf(raw: string): string | null {
  const bare = raw.trim().replace(/^platform-v/, "");
  return BARE_VERSION.test(bare) ? bare : null;
}

/** `<ref>:<tag>` for a manifest's installer component. */
function engineImageOf(m: SealedManifest): string {
  const c = m.manifest.components.installer;
  return `${c.ref}:${c.tag}`;
}

export async function resolveLaunchTarget(args: ResolveLaunchTargetArgs): Promise<LaunchTarget> {
  const { pinnedInstallerRef, pinnedPlatformVersion, fetchSealed } = args;
  const release = typeof args.release === "string" && args.release.length > 0 ? args.release : undefined;
  const tagOverride = typeof args.installerTag === "string" && args.installerTag.length > 0 ? args.installerTag : undefined;
  const imageOverride = typeof args.installerImage === "string" && args.installerImage.length > 0 ? args.installerImage : undefined;

  // The pinned ref written out longhand is not an override of anything — several
  // runbooks and scripts pass it explicitly. Treating it as one would silently
  // downgrade an ordinary install to unreleased-debug, which is a worse version of
  // the silence this whole function exists to remove.
  const pinnedRepo = pinnedInstallerRef.slice(0, pinnedInstallerRef.lastIndexOf(":"));
  // ONE effective override, resolved with launch.ts's own precedence (a full
  // --installer-image wins over --installer-tag). Comparing the two flags
  // separately let a launch pass both, have only the losing one match, and be
  // accepted — silently ignoring the flag that actually took effect.
  const overrideEngine = imageOverride ?? (tagOverride !== undefined ? `${pinnedRepo}:${tagOverride}` : undefined);
  const explicitEngine = overrideEngine !== undefined && overrideEngine !== pinnedInstallerRef;
  const overrideFlag = imageOverride !== undefined ? "--installer-image" : "--installer-tag";

  if (release !== undefined) {
    const bare = bareVersionOf(release);
    if (!bare) {
      throw new LocalCliError({
        code: "BOOTSTRAP_RELEASE_INVALID",
        message: `--release must name a platform release, got ${JSON.stringify(release)}.`,
        hint: "Pass a release version, e.g. --release 0.7.2 (or --release platform-v0.7.2). There is no 'latest' here on purpose: an install pins the release it installs.",
      });
    }
    const sealedManifest = await fetchSealed(bare);
    const engineFromManifest = engineImageOf(sealedManifest);

    // An override that names exactly what the manifest names is not a conflict —
    // it is the same instruction twice. Anything else is two answers to one
    // question, and guessing which the operator meant is how an install ends up
    // being something nobody asked for.
    if (explicitEngine && overrideEngine !== engineFromManifest) {
      throw new LocalCliError({
        code: "BOOTSTRAP_RELEASE_OVERRIDE_CONFLICT",
        message:
          `--release ${bare} names the engine ${engineFromManifest}, but ${overrideFlag} names ${overrideEngine}.`,
        hint: `Drop the override to install release ${bare} as published, or drop --release to launch that engine as an unreleased-debug install.`,
      });
    }

    assertManifestSelfConsistent(sealedManifest, bare);
    return { kind: "released", version: bare, engineImage: engineFromManifest, manifestSha256: sealedManifest.digest };
  }

  // `explicitEngine` carries the `!== undefined` half of its own definition, and
  // TypeScript narrows through it — re-testing here would be dead code.
  if (explicitEngine) {
    const engineImage = overrideEngine;
    return {
      kind: "unreleased-debug",
      engineImage,
      reason:
        `${overrideFlag} points this install at ${engineImage}, which no release manifest names. ` +
        "The install will deploy what that engine carries and will NOT claim a platform release version.",
    };
  }

  // The paved path: this CLI's own pinned release.
  const sealedManifest = await fetchSealed(pinnedPlatformVersion);
  assertManifestSelfConsistent(sealedManifest, pinnedPlatformVersion);

  // The reciprocal check, at the one moment it is free. This CLI is about to
  // launch the engine it baked while telling that engine it is installing
  // `pinnedPlatformVersion`. If the release's manifest names a DIFFERENT engine,
  // those two statements cannot both be true — and believing them both is exactly
  // the two-record defect, just one step earlier in the pipeline. A cut should
  // never publish this state (verify-cut asserts the same pairing); if one did,
  // the founder hears about it before a single resource is created.
  const engineFromManifest = engineImageOf(sealedManifest);
  if (engineFromManifest !== pinnedInstallerRef) {
    throw new LocalCliError({
      code: "BOOTSTRAP_ENGINE_MANIFEST_MISMATCH",
      message:
        `This CLI installs platform ${pinnedPlatformVersion} with the engine ${pinnedInstallerRef}, ` +
        `but ${pinnedPlatformVersion}'s manifest names ${engineFromManifest}. The release and this CLI disagree about which engine runs it.`,
      hint:
        "Get the newest CLI with `npm install -g @m8t-stack/cli` — the CLI is what pins the engine. " +
        `If that does not resolve it, install the release explicitly with \`m8t bootstrap launch --release ${pinnedPlatformVersion}\`, which takes the engine from the manifest.`,
    });
  }

  return {
    kind: "released",
    version: pinnedPlatformVersion,
    engineImage: pinnedInstallerRef,
    manifestSha256: sealedManifest.digest,
  };
}

/**
 * A manifest fetched for version X must say it is X.
 *
 * The engine checks this too, and deliberately: these are two independent readers
 * and the check costs nothing in either. Here it fires before any resource exists,
 * which is the difference between a usage error and a half-built install.
 */
function assertManifestSelfConsistent(sealedManifest: SealedManifest, expected: string): void {
  const declared = sealedManifest.manifest.platform.version;
  if (declared !== expected) {
    throw new LocalCliError({
      code: "BOOTSTRAP_MANIFEST_VERSION_MISMATCH",
      message: `Asked the channel for platform ${expected}, but the manifest it returned declares ${declared}.`,
      hint: "The channel pointer may be misconfigured, or that release's manifest asset is wrong. Nothing was installed.",
    });
  }
}
